"use client";

import { useState } from "react";
import { FeaturedHero } from "./featured-hero";
import { VideoRow } from "./video-row";
import { VideoModal } from "./video-modal";
import type { YouTubePlaylist, YouTubePlaylistItem } from "@/lib/youtube";

interface PlaylistWithVideos extends Omit<YouTubePlaylist, "videos"> {
  videos: YouTubePlaylistItem[];
}

interface VideoBrowserTranslations {
  recentlyAdded: string;
  viewAll?: string;
  all?: string;
  new?: string;
  officialChannel?: string;
  watchBadge?: string;
}

interface VideoBrowserProps {
  playlists: PlaylistWithVideos[];
  allVideos: YouTubePlaylistItem[];
  translations?: VideoBrowserTranslations;
}

const defaultTranslations: VideoBrowserTranslations = {
  recentlyAdded: "Recently Added",
  viewAll: "VIEW ALL",
  all: "ALL",
  new: "New",
  officialChannel: "Official Channel",
  watchBadge: "WATCH",
};

export const VideoBrowser = ({
  playlists,
  allVideos,
  translations = defaultTranslations,
}: VideoBrowserProps) => {
  const [selectedVideo, setSelectedVideo] = useState<{
    videoId: string;
    title: string;
  } | null>(null);

  const handlePlayVideo = (videoId: string, title: string) => {
    setSelectedVideo({ videoId, title });
  };

  const handleCloseModal = () => {
    setSelectedVideo(null);
  };

  // Get recent videos (first 10 from all videos combined)
  const recentVideos = allVideos.slice(0, 12);

  return (
    <div className="min-h-screen">
      {/* Featured Hero - Rotates through recent videos */}
      <FeaturedHero videos={recentVideos.slice(0, 5)} onPlayVideo={handlePlayVideo} />

      {/* Video Rows */}
      <div className="relative z-10 -mt-32 pb-20 space-y-8 bg-gradient-to-b from-transparent via-zinc-100 dark:via-brand-black to-zinc-100 dark:to-brand-black">
        {/* Recent/All Videos Row */}
        {recentVideos.length > 0 && (
          <VideoRow
            title={translations.recentlyAdded}
            videos={recentVideos}
            onPlayVideo={handlePlayVideo}
            translations={{
              viewAll: translations.viewAll,
              all: translations.all,
              new: translations.new,
              officialChannel: translations.officialChannel,
              watch: translations.watchBadge,
            }}
          />
        )}

        {/* Playlist Rows */}
        {playlists.map((playlist) => (
          playlist.videos.length > 0 && (
            <VideoRow
              key={playlist.id}
              title={playlist.title}
              videos={playlist.videos}
              onPlayVideo={handlePlayVideo}
              playlistId={playlist.id}
              translations={{
                viewAll: translations.viewAll,
                all: translations.all,
                new: translations.new,
                officialChannel: translations.officialChannel,
                watch: translations.watchBadge,
              }}
            />
          )
        ))}
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={!!selectedVideo}
        onClose={handleCloseModal}
        videoId={selectedVideo?.videoId || null}
        title={selectedVideo?.title || ""}
      />
    </div>
  );
};
